import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronRight, BookOpen, Mic, Sparkles, BarChart3, CreditCard, Globe, Settings, Send } from 'lucide-react';

const categories = [
  { id: 'getting-started', icon: BookOpen, title: 'Getting Started', desc: 'Account setup, first article, and basics',
    articles: [
      { slug: 'create-account', title: 'How to create your Stilos account', reading: '2 min' },
      { slug: 'first-article', title: 'Creating your first article with AI', reading: '3 min' },
      { slug: 'understand-credits', title: 'Understanding credits and how they work', reading: '2 min' },
      { slug: 'dashboard-overview', title: 'Dashboard overview and navigation', reading: '3 min' },
      { slug: 'install-pwa', title: 'Installing Stilos as a mobile app (PWA)', reading: '2 min' },
    ]},
  { id: 'voice-ai', icon: Mic, title: 'Voice to Article', desc: 'Recording, transcription, and voice-first workflow',
    articles: [
      { slug: 'record-voice', title: 'How to record a voice note', reading: '2 min' },
      { slug: 'voice-tips', title: 'Tips for better voice-to-article results', reading: '4 min' },
      { slug: 'supported-formats', title: 'Supported audio formats and devices', reading: '2 min' },
      { slug: 'voice-troubleshooting', title: 'Troubleshooting voice recording issues', reading: '3 min' },
    ]},
  { id: 'ai-content', icon: Sparkles, title: 'AI Article Generation', desc: 'Titles, keywords, tone, and generation settings',
    articles: [
      { slug: 'generate-article', title: 'How to generate an article from a title', reading: '3 min' },
      { slug: 'choose-keywords', title: 'Choosing effective keywords', reading: '4 min' },
      { slug: 'tone-settings', title: 'Understanding tone and style settings', reading: '3 min' },
      { slug: 'edit-generated', title: 'Editing and improving generated articles', reading: '3 min' },
    ]},
  { id: 'seo-aeo', icon: BarChart3, title: 'SEO & AEO/GEO', desc: 'Scores, optimization, and AI-ready content',
    articles: [
      { slug: 'seo-score', title: 'Understanding your SEO score', reading: '3 min' },
      { slug: 'aeo-geo', title: 'What is AEO/GEO and why it matters', reading: '5 min' },
      { slug: 'improve-score', title: 'How to improve your SEO and AEO/GEO scores', reading: '4 min' },
      { slug: 'faq-optimization', title: 'FAQ sections for AI citation', reading: '3 min' },
    ]},
  { id: 'brand', icon: Globe, title: 'Brand Voice & Reporting', desc: 'Brand profiles, kits, and actionable reports',
    articles: [
      { slug: 'create-brand-voice', title: 'Creating a brand voice profile', reading: '3 min' },
      { slug: 'brand-report', title: 'Generating your Brand Report & Kit', reading: '4 min' },
      { slug: 'use-brand-report', title: 'How to use your Brand Report with designers', reading: '5 min' },
      { slug: 'brand-article', title: 'Using brand voice in article generation', reading: '3 min' },
    ]},
  { id: 'publishing', icon: Send, title: 'Publishing & Scheduling', desc: 'WordPress, calendar, and multi-platform',
    articles: [
      { slug: 'connect-wordpress', title: 'Connecting your WordPress site', reading: '3 min' },
      { slug: 'publish-article', title: 'Publishing an article to WordPress', reading: '2 min' },
      { slug: 'schedule-posts', title: 'Scheduling posts with the content calendar', reading: '3 min' },
      { slug: 'app-passwords', title: 'Setting up WordPress Application Passwords', reading: '3 min' },
    ]},
  { id: 'billing', icon: CreditCard, title: 'Billing & Subscriptions', desc: 'Plans, credits, payments, and promo codes',
    articles: [
      { slug: 'plans-overview', title: 'Plans and pricing overview', reading: '3 min' },
      { slug: 'buy-credits', title: 'How to buy credit packs', reading: '2 min' },
      { slug: 'redeem-promo', title: 'Redeeming a promo code', reading: '2 min' },
      { slug: 'manage-subscription', title: 'Managing your subscription', reading: '3 min' },
    ]},
  { id: 'account', icon: Settings, title: 'Account & Settings', desc: 'Profile, notifications, data export, and security',
    articles: [
      { slug: 'update-profile', title: 'Updating your profile information', reading: '2 min' },
      { slug: 'email-notifications', title: 'Managing email notifications', reading: '2 min' },
      { slug: 'export-data', title: 'Exporting your data', reading: '2 min' },
      { slug: 'delete-account', title: 'Deleting your account', reading: '2 min' },
    ]},
];

const KnowledgeBasePage = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  const allArticles = categories.flatMap(c => c.articles.map(a => ({ ...a, category: c.title, categoryId: c.id })));
  const filtered = search ? allArticles.filter(a => a.title.toLowerCase().includes(search.toLowerCase())) : [];

  if (activeCategory) {
    const cat = categories.find(c => c.id === activeCategory);
    return (
      <div className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => setActiveCategory(null)} className="flex items-center gap-2 text-[var(--color-muted)] hover:text-[var(--color-charcoal)] mb-8 text-sm transition-colors">
            &larr; Back to Knowledge Base
          </button>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--color-surface)]">
              <cat.icon className="w-5 h-5 text-[var(--color-coral)]" />
            </div>
            <div>
              <h1 className="font-serif text-2xl">{cat.title}</h1>
              <p className="text-xs text-[var(--color-muted)]">{cat.articles.length} articles</p>
            </div>
          </div>
          <div className="space-y-2">
            {cat.articles.map((article, idx) => (
              <div key={idx} className="card p-5 card-hover cursor-pointer">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-sm font-medium">{article.title}</h3>
                    <p className="text-[10px] text-[var(--color-muted)] mt-0.5">{article.reading} read</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--color-muted)]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">HELP CENTER</span>
            <h1 className="font-serif text-4xl md:text-5xl mb-4">Knowledge Base</h1>
            <p className="text-base text-[var(--color-muted)] mb-8">Find answers, guides, and best practices for Stilos.</p>
            
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted)]" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                className="input pl-11 text-sm" placeholder="Search articles..." data-testid="kb-search" />
            </div>

            {search && (
              <div className="max-w-xl mx-auto mt-4 text-left">
                {filtered.length === 0 ? (
                  <p className="text-xs text-[var(--color-muted)] text-center py-4">No articles found for "{search}"</p>
                ) : (
                  <div className="card divide-y divide-[var(--color-border)]">
                    {filtered.slice(0, 8).map((article, idx) => (
                      <div key={idx} className="p-4 hover:bg-[var(--color-surface)] transition-colors cursor-pointer">
                        <p className="text-sm font-medium">{article.title}</p>
                        <p className="text-[10px] text-[var(--color-muted)] mt-0.5">{article.category} &middot; {article.reading} read</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <div key={cat.id} onClick={() => setActiveCategory(cat.id)} className="card card-hover p-6 cursor-pointer" data-testid={`kb-cat-${cat.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--color-surface)] flex-shrink-0">
                      <Icon className="w-5 h-5 text-[var(--color-coral)]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-sm font-medium mb-0.5">{cat.title}</h3>
                      <p className="text-[10px] text-[var(--color-muted)]">{cat.desc}</p>
                      <p className="text-[10px] text-[var(--color-coral)] mt-1 font-medium">{cat.articles.length} articles &rarr;</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-xl mb-3">Can't find what you're looking for?</h2>
          <p className="text-sm text-[var(--color-muted)] mb-6">Our support team is here to help.</p>
          <Link to="/contact" className="btn btn-secondary">Contact Support</Link>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeBasePage;
